import GiftOrderApi from '@apis/gift/GiftOrderApi';
import { datadogRum } from '@datadog/browser-rum';
import useFingerPay from '@hooks/order/useFingerPay';
import useNicePay from '@hooks/order/useNicePay';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { GiftItemRow, GiftOrderRequest } from '@models/gift';
import GiftOrderContext from '@providers/GiftOrderProvider/GiftOrderContext';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

function useGiftOrder(giftItem: GiftItemRow, qty: number, giftCardImageId?: string) {
    const { state, enabled } = useContext(GiftOrderContext);
    const projectId = useProjectId();
    const toast = useToast();
    const openFingerPay = useFingerPay();
    const openNicePay = useNicePay();
    const project = useProject();

    const { t } = useTranslation('gift');
    const { mutate } = useMutation({
        mutationFn: GiftOrderApi.createGiftOrder,
        onSuccess(res) {
            const {
                pageUrl, orderNo, mallId, requestedAt, hashString,
            } = res;
            if (pageUrl) {
                window.location.href = pageUrl;
            } else if (orderNo && mallId && requestedAt && hashString) {
                const amount = giftItem.salePrice * qty * state.giftRecipients.length;
                const itemName = t('giftOrder.giftOrderItem', { name: giftItem.name, count: state.giftRecipients.length });
                if (project?.pgVendor === 'FINGERPAY') {
                    openFingerPay({
                        orderNo,
                        mallId,
                        requestedAt,
                        hashString,
                        paymentAmount: amount,
                        itemName,
                        returnUri: '/gift-order/payment/finger-pay',
                    });
                } else if (project?.pgVendor === 'NICEPAY') {
                    openNicePay({
                        paymentAmount: amount,
                        itemName,
                        returnUri: '/gift-order/payment/nice-pay',
                        orderNo,
                        mallId,
                        requestedAt,
                        hashString,
                    });
                } else {
                    toast.error('pg 설정을 확인해주세요.');
                }
            }
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            toast.error(e.response?.data.message || t('giftOrder.errorMessage'));
        },
    });
    const handleOrder = () => {
        if (!enabled) {
            return;
        }
        const {
            message, userPhone, userName, giftRecipients, cardName,
        } = state;
        const amount = giftItem.salePrice * qty * giftRecipients.length;
        const request: GiftOrderRequest = {
            projectId,
            userName,
            userPhone: userPhone.replace(/-/gi, ''),
            message,
            giftRecipients: giftRecipients.map((it) => ({
                name: it.name,
                phoneNo: it.phoneNo.replace(/-/gi, ''),
            })),
            paymentMethod: 'CREDIT_CARD',
            amount,
            giftOrderItems: [{ giftItemId: giftItem.id, count: qty }],
            appScheme: 'com.waldlust.platform',
            cardName,
            giftCardImageId,
        };
        if (import.meta.env.MODE !== 'development') {
            datadogRum.addAction('giftOrderCheckout', {
                item: giftItem.id,
                amount,
            });
        }
        mutate(request);
    };
    return {
        handleOrder, state, enabled,
    };
}

export default useGiftOrder;
